import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Dropdown,
  DropdownButton,
  Spinner,
} from "react-bootstrap";

import TopLimitDropdown from "../../../../../common/TopLimitDropdown";
import AccordionComponent from "../../../../../common/Accordion";
import { useDispatch } from "react-redux";
import {
  setIsDashboard,
  setNewCaseInFoID,
} from "../../../../../redux/slice/CaseHeaderSlice";
import { formatDate } from "react-datepicker/dist/date_utils";
import { formattedDate } from "../../../../../utils/dateUtils";
import { getRORRushNotesSummary } from "../../../../../services/RORDashboardAPI";
import { WEB_ROLE } from "../../../../../common/Constant";
import { getHistorianRushNotesSummary } from "../../../../../services/HistorianDashboardAPI";
import { getEditorRushNotesSummary } from "../../../../../services/EditorDashboardAPI";
import AllRushNotesDetails from "../AllRushNotesDetails";
import AppointmentActionDrawer from "../../../../../modules/Appointments/components/AppointmentActionDrawer";

interface RORRushNotesSummaryProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
  setShowRushNotesSummary: any;
  userRole?: string;
}

const RORRushNotesSummary: React.FC<RORRushNotesSummaryProps> = ({
  openDropdown,
  setOpenDropdown,
  setShowRushNotesSummary,
  userRole,
}) => {
  const dispatch = useDispatch();
  const [limit, setLimit] = useState<number>(5);
  const [limitLabel, setLimitLabel] = useState<string>("Top 5");
  const [rushNotesData, setRushNotesData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [visibleHeaderIndex, setVisibleHeaderIndex] = useState<boolean>(false);
  const [showRushNotesDrawer, setShowRushNotesDrawer] =
    useState<boolean>(false);

  const fetchRushNotes = async (limitValue: number) => {
    setLoading(true);

    try {
      let data;

      switch (userRole) {
        case WEB_ROLE.Record_Reviewer:
          data = await getRORRushNotesSummary(limitValue);
          break;

        case WEB_ROLE.Historian:
          data = await getHistorianRushNotesSummary(limitValue);
          break;

        case WEB_ROLE.Editor:
          data = await getEditorRushNotesSummary(limitValue);
          break;

        default:
          data = [];
      }

      setRushNotesData(data);
    } catch (error) {
      console.error("Failed to fetch latest cases:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRushNotes(limit);
  }, [limit]);

  const handleLimitChange = (value: number, label: string) => {
    setLimit(value);
    setLimitLabel(label);
  };

  const handleCloseRushNotes = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLimit(5);
    setLimitLabel("Top 5");
    setShowRushNotesSummary(false);
  };

  const [LatestCaseKey, setLatestCaseKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (LatestCaseKey === key) {
      return; // do nothing
    }
    setLatestCaseKey(key || "0");
  };

  const LatestCaseAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={LatestCaseKey}
      onSelect={handleBodyAccordionSelect}
    />
  );

  return (
    <>
      {LatestCaseAccordion([
        {
          title: (
            <div className="d-flex align-items-center w-100">
              {/* Left Title Section */}
              <div className="me-auto d-flex flex-column">
                <span className="fw-semibold">Rush Notes</span>
                <span
                  style={{
                    fontSize: "12px",
                    fontStyle: "italic",
                    color: "#6c757d",
                  }}
                >
                  Case Requiring Immediate Action
                </span>
              </div>

              {/* Dropdown */}
              <div onClick={(e) => e.stopPropagation()}>
                <TopLimitDropdown
                  title={limitLabel}
                  onSelect={handleLimitChange}
                  show={openDropdown === "RORcaseRushNotes"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "RORcaseRushNotes" : null)
                  }
                />
              </div>

              {/* Back Button */}
              <Button
                variant="outline-info"
                className="d-flex align-items-center ms-2 btn-text-border"
                onClick={handleCloseRushNotes}
              >
                <span className="material-icons me-1">arrow_back</span>
                Back
              </Button>
            </div>
          ),
          content: (
            <>
              <Card className="border-0 ">
                <Card.Body className="d-flex flex-column p-0 bg-theme">
                  <div
                    style={{
                      maxHeight: limit > 5 ? "282px" : "auto",
                      overflowY: "auto",
                    }}
                    className="d-flex flex-column gap-1 flex-grow-1"
                  >
                    {rushNotesData.length === 0 ? (
                      <p>No cases found.</p>
                    ) : (
                      rushNotesData.map((item, index) => (
                        <div
                          key={index}
                          className="p-2 mb-1 slide-up-onload"
                          style={{
                            background: "#ffffff",
                            borderRadius: "6px",
                            border: "1px solid #e6edf5",
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 120px", // fixed width for date column
                              columnGap: "0px",
                              fontSize: "13px",
                              lineHeight: "1.2",
                              alignItems: "stretch", // important for full height border
                            }}
                          >
                            {/* LEFT SIDE (Patient + Claim) */}
                            <div
                              style={{
                                paddingRight: "12px",
                                lineHeight: "1.1",
                              }}
                            >
                              <div className="text-truncate fw-medium m-0 p-0">
                                {item.patientName || "-"}
                              </div>

                              <a
                                href="#/search-case"
                                className="dashbardCaseAnchr text-decoration-underline text-truncate m-0 p-0"
                                style={{ fontSize: "12.5px" }}
                                onClick={() => {
                                  dispatch(setIsDashboard(true));
                                  dispatch(setNewCaseInFoID(item.caseInfoId));
                                  setVisibleHeaderIndex(true);
                                }}
                              >
                                {item.accountNo || "-"}
                              </a>
                            </div>

                            {/* RIGHT SIDE (Date Centered Both Ways) */}
                            <div
                              style={{
                                borderLeft: "1px solid #e6edf5",
                                display: "flex",
                                alignItems: "center", // vertical center
                                justifyContent: "center", // horizontal center
                                textAlign: "center",
                                padding: "0 8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formattedDate(item.lastModifiedDate)}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {rushNotesData.length > 0 && (
                    <div className="text-end p-2 border-top bg-white rounded-bottom">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none fw-semibold"
                        onClick={() => setShowRushNotesDrawer(true)}
                      >
                        View More
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
              <AppointmentActionDrawer
                open={showRushNotesDrawer}
                onClose={() => setShowRushNotesDrawer(false)}
                title={
                  <div className="d-flex align-items-center">
                    <span className="rushIcon">
                      <span className="material-symbols-outlined me-2 rushicon p-1 heartbeat">
                        sprint
                      </span>
                    </span>
                    <span className="rushTitle"> Rush Notes</span>
                  </div>
                }
              >
                <AllRushNotesDetails />
              </AppointmentActionDrawer>
            </>
          ),
        },
      ])}
    </>
  );
};

export default RORRushNotesSummary;
